import {
  Avatar as MantineAvatar,
  AvatarProps as MantineAvatarProps,
} from '@mantine/core';
import { ReactNode } from 'react';

import styles from './Avatar.module.css';

export type AvatarSize = 'sm' | 'md' | 'lg';

export type AvatarColor =
  | 'indigo'
  | 'blue'
  | 'green'
  | 'yellow'
  | 'red'
  | 'gray'
  | 'mint'
  | 'orange'
  | 'pink'
  | 'steel';

/**
 * Generate initials from first and last name.
 * @example getInitials('John', 'Doe') // 'JD'
 */
export function getInitials(
  firstName?: string | null,
  lastName?: string | null
): string {
  return `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase();
}

export interface AvatarProps extends Omit<
  MantineAvatarProps,
  'variant' | 'color' | 'styles' | 'classNames' | 'style' | 'size' | 'radius'
> {
  /** Avatar size: sm (24px), md (32px), lg (40px). Default: md */
  size?: AvatarSize;
  /** Avatar color variant. Default: indigo */
  color?: AvatarColor;
  /**
   * Initials to render when no `src` image is available.
   * Use `getInitials(firstName, lastName)` to build them.
   */
  children?: ReactNode;
}

export const Avatar = ({
  size = 'md',
  color = 'indigo',
  children,
  ...props
}: AvatarProps) => {
  return (
    <MantineAvatar
      unstyled
      classNames={{
        root: styles.root,
        image: styles.image,
        placeholder: styles.placeholder,
      }}
      data-size={size}
      data-color={color}
      {...props}
    >
      {children}
    </MantineAvatar>
  );
};
